import BacktestLoader from './components/BacktestLoader';
import EquityChart from './components/EquityChart';

export default function App() {
  return (
    <div className="page">
      <BacktestLoader />

      {/* ================= 1) HERO / ABOVE THE FOLD ================= */}
      {/* ================= 1) HERO / ABOVE THE FOLD ================= */}
      <section className="heroFull">
        <div className="inner">
          <header className="hero heroWide">
            <div className="heroTop">
              <div className="brandPill">SPX Gamma</div>
              <div className="heroMeta">GEMIC • 1-DTE • Expected-Move Based • Defined Risk</div>
            </div>

            <h1 className="heroTitle">GEMIC — Gamma Expected Move Iron Condor</h1>

            <p className="heroSubtitle">
              A daily 1-DTE SPX iron condor strategy built to anchor the SPX Gamma premium-selling suite with systematic,
              expected-move-based trade opportunities.
            </p>

            <div className="heroBody">
              <p className="heroLead">
                <strong>GEMIC (Gamma Expected Move Iron Condor)</strong> is the daily 1-DTE premium-selling component of
                the broader <strong>SPX Gamma Premium Sellers strategy suite</strong>. Where <strong>MEIC</strong> focuses
                on intraday 0-DTE iron condor opportunities, <strong>ACCE</strong> selectively targets more aggressive
                1-DTE dislocations, and <strong>ARGOS</strong> adapts across multi-day regime windows, GEMIC provides the
                core daily structure: a defined-risk SPX iron condor built around the market’s projected next-session move.
              </p>

              <div style={{ height: 5 }} />


              <p className="heroLead">
                GEMIC does not start with a directional prediction. It starts with the expected move. Using
                <strong> SPX</strong>, <strong>VIX1D</strong>, <strong>VIX</strong>, and <strong>VVIX</strong>, the strategy estimates
                the range the market is pricing for the next trading session, then uses that structure to place the short put and
                short call while the long wings define the outer risk boundary. Inside the larger SPX Gamma framework, GEMIC serves
                as the systematic daily premium engine: a repeatable 1-DTE trade with defined entry, defined expiration, defined max
                profit, defined max loss, and a clear expected-move structure behind every position.
              </p>

              <div style={{ height: 5 }} />

              <div style={{ height: 5 }} />

            </div>

            <div className="heroActions">
              <a className="btnPrimary" href="#performance">View Performance</a>
              <a className="btnGhost" href="https://www.spxgamma.com/" target="_blank" rel="noopener noreferrer">
                Access SPX Gamma
              </a>
            </div>
          </header>
        </div>
      </section>

      {/* ================= 2) QUICK SUMMARY STRIP ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="paperStrip">
            <div className="stripItem">
              <div className="stripCallout">Designed For</div>
              <p className="text">
                Traders who want a systematic, defined-risk approach to daily SPX premium selling using expected move
                structure.
              </p>
            </div>

            <div className="stripItem">
              <div className="stripCallout">Approach</div>
              <p className="text">
                <strong>1-DTE SPX Iron Condor</strong>. Short strikes are derived from the projected next-day expected
                move.
              </p>
            </div>

            <div className="stripItem">
              <div className="stripCallout">Objective</div>
              <p className="text">
                Capture premium when SPX remains inside the expected-move framework while keeping max loss known before
                entry.
              </p>
            </div>

            <div className="stripCallout">
              GEMIC is a structured daily premium-selling framework. It uses volatility-informed expected move bands to
              define the trade, then executes a controlled iron condor with fixed-width risk.
            </div>
          </div>
        </div>
      </section>

      {/* ================= 3) PERFORMANCE ================= */}
      <section id="performance" className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Performance</h2>
          </div>

          <div className="lede">
            <p className="text">
              GEMIC’s performance reflects a systematic 1-DTE iron condor process built around expected move structure.
              The framework does not need to predict direction. It needs to define a reasonable range, collect premium,
              and control risk through fixed-width wings.
            </p>
          </div>

          <div className="feature">
            <div className="featureBody">
              <EquityChart />
            </div>
          </div>
        </div>
      </section>

      {/* ================= 4) STRATEGY OVERVIEW ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Strategy Overview</h2>
          </div>

          <div className="paperLong">
            <div className="paperBlock">
              <h3 className="subTitle">Strategy Snapshot</h3>

              <p className="text">
                GEMIC operates exclusively in SPX using defined-risk 1-DTE iron condors. The structure is built around
                the next-session expected move, with short strikes placed near the projected expected-move boundaries
                rather than arbitrary fixed offsets from spot. The expected move model uses <strong>SPX</strong>, <strong>VIX1D</strong>, <strong>VIX</strong>, and{' '}
                <strong>VVIX</strong> to estimate the range the market is pricing for the next trading session. That
                range becomes the foundation for strike placement.
              </p>

              <p className="text">
                The trade is credit-based and defined-risk. GEMIC uses <strong>20-point-wide wings</strong>:
                the long put is placed 20 points below the short put, and the long call is placed 20 points above the
                short call. Since each spread is 20 points wide, the maximum theoretical risk is capped at
                <strong> $2,000 minus the credit received</strong>. For example, if GEMIC collects a
                <strong> $550 credit</strong>, the maximum risk on the structure is <strong>$1,450</strong>.
              </p>

              <div className="stripCallout">
                GEMIC is designed to turn the expected move into a repeatable trade structure: expected-move-based short
                strikes, fixed-width wings, defined risk, and systematic premium collection.
              </div>
            </div>
          </div>

          <div className="paperLong">
            <div className="paperBlock">
              <h3 className="subTitle">The Core Concept</h3>

              <p className="text">
                Most short-premium strategies start with a directional opinion. GEMIC starts with structure. The goal is not to
                predict whether SPX will finish higher or lower the next day. The goal is to determine whether the market’s
                expected move creates a reasonable, defined-risk opportunity to sell premium around the projected range.
              </p>

              <p className="text">
                The short strikes define the premium collection zone. The long wings define the outer risk boundary. The credit
                received defines the maximum reward. Before the trade is entered, GEMIC knows the structure, the
                maximum profit, the maximum loss, and the price area where the trade is expected to perform best.
              </p>

              <div style={{ height: 10 }} />

              <p className="stripCallout">
                GEMIC is not a directional bet. It is a structured 1-DTE premium-selling framework built around expected move,
                defined risk, and repeatable execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 5) TRADE TIMING + EXECUTION ================= */}
      {/* ================= 5) TRADE TIMING + EXECUTION ================= */}
      <section className="sectionFull sectionBand">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Trade Timing & Execution Flow</h2>
            <p className="text">
              GEMIC is structured as a daily 1-DTE process. The system evaluates the market near the close, builds the
              expected move framework, and prepares the next trading session’s iron condor structure.
            </p>
          </div>

          <div className="grid2">
            <div className="paperCard">
              <div className="paperHead">
                <div className="paperTitle">Daily Trade Cycle</div>
                <div className="paperTag">5 Min Before Close → Next Session</div>
              </div>

              <div className="filterStack">
                <div className="filterItem">
                  <div className="filterIndex">1</div>
                  <div className="filterBody">
                    <div className="filterTitle">Market Data Snapshot</div>
                    <div className="filterText">
                      Near the end of the session, SPX and volatility data are captured to estimate the next-session
                      expected move.
                    </div>
                  </div>
                </div>

                <div className="filterItem">
                  <div className="filterIndex">2</div>
                  <div className="filterBody">
                    <div className="filterTitle">Expected Move Calculation</div>
                    <div className="filterText">
                      The model blends short-term volatility, broader volatility context, and VVIX refinement to define
                      the projected 1-DTE range.
                    </div>
                  </div>
                </div>

                <div className="filterItem">
                  <div className="filterIndex">3</div>
                  <div className="filterBody">
                    <div className="filterTitle">Strike Construction</div>
                    <div className="filterText">
                      Short strikes are selected using SPX Gamma proprietary expected move levels. Long wings are placed
                      20 points beyond the short strikes to create the defined-risk iron condor.
                    </div>
                  </div>
                </div>

                <div className="filterItem">
                  <div className="filterIndex">4</div>
                  <div className="filterBody">
                    <div className="filterTitle">Next-Day Expiration</div>
                    <div className="filterText">
                      The position is initiated 5 minutes before the close and settles against the next trading day’s
                      expiration.
                    </div>
                  </div>
                </div>

                <div className="filterDecision">
                  <div className="filterDecisionTitle">Execution Principle</div>
                  <div className="stripCallout">
                    The full structure is known before entry: short put, long put, short call, long call, credit received,
                    max profit, and max loss.
                  </div>
                </div>
              </div>
            </div>

            <div className="paperCard">
              <div className="paperHead">
                <div className="paperTitle">Trade Structure</div>
                <div className="paperTag">1-DTE Iron Condor</div>
              </div>

              <p className="text" style={{ marginBottom: 14 }}>
                GEMIC enters a four-leg SPX iron condor. The short strikes are selected from SPX Gamma proprietary expected
                move levels, while the long wings define the outer risk boundary.
              </p>

              <figure className="gemicTradeFigure">
                <div className="gemicTradeImageWrap">
                  <img
                    className="gemicTradeImage"
                    src="/GEMIC-trade-image.png"
                    alt="GEMIC 1-DTE SPX iron condor trade structure"
                  />
                </div>

                <figcaption className="stripCallout gemicTradeCaption">
                  <strong>Key point:</strong> the expected move helps build the trade, but the actual option strikes determine
                the final payoff.
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 6) MANAGEMENT + SETTLEMENT ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Position Management & Settlement</h2>
            <p className="text">
              GEMIC was backtested using a simple hold-to-expiration approach. This creates a clean baseline for evaluating the
              strategy because every trade is allowed to settle against its final 1-DTE payoff structure without discretionary
              intervention.
            </p>
            </div>

            <div className="grid2">
              <div className="paperCard">
                <div className="paperHead">
                  <div className="paperTitle">Management Framework</div>
                  <div className="paperTag">Baseline: Hold to OPEX</div>
                </div>

                <p className="text" style={{ marginBottom: 10 }}>
                  For performance measurement, GEMIC positions were modeled as <strong>unmanaged trades held through expiration</strong>.
                  This means the backtest does not assume profit-taking, stop-loss exits, rolling, adjusting, or discretionary trade
                  management.
                </p>

                <p className="text" style={{ marginBottom: 10 }}>
                  Active management is still an option for traders who prefer to reduce risk, lock in profits, or respond to fast
                  directional moves. In some cases, managing early can protect open profit or reduce exposure before expiration.
                  However, management can also work against the trader if SPX moves back inside the expected-move structure after an
                  early exit or adjustment.
                </p>



                <p className="text">
                  The framework is not dependent on averaging down, rolling indefinitely, or adding unplanned exposure. Traders may choose to
                  manage positions more actively, but managed results will naturally deviate from the historical hold-to-expiration
                  results shown on this page.
                </p>
              </div>

            <div className="paperCard">
              <div className="paperHead">
                <div className="paperTitle">Expiration Logic</div>
                <div className="paperTag">Close vs Short Strikes</div>
              </div>

              <p className="text" style={{ marginBottom: 10 }}>
                At expiration, the position is evaluated against the actual SPX settlement or closing level and the
                final option strikes.
              </p>

              <p className="text" style={{ marginBottom: 10 }}>
                A full win occurs when SPX finishes between the short put and short call. Partial loss or full loss
                depends on how far price moves beyond the short strike and whether it reaches the long wing.
              </p>

              <div className="stripCallout" style={{ marginTop: 8, marginBottom: 12 }}>
                <strong>Important:</strong> the expected move helps build the trade, but the actual option strikes
                determine the final payoff.
              </div>

              <p className="text">
                Settlement should be based on the actual short and long strike structure, not simply whether price
                touched or exceeded the original expected-move band.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 7) MONTE CARLO VALIDATION ================= */}
      <section className="sectionFull sectionBand">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Monte Carlo Validation</h2>
          </div>

          <div className="split split--mc">
            <div className="splitLeft">
              <p className="text">
                Monte Carlo testing randomizes the order of the GEMIC trade outcomes and replays the strategy across
                thousands of alternate paths. This helps determine whether the strategy’s edge depends on one favorable
                historical sequence or remains resilient across many possible orderings.
              </p>

              <p className="text">
                The actual GEMIC backtest produced <strong>558 trades</strong>, with <strong>448 wins</strong> and{' '}
                <strong>110 losses</strong>, resulting in an actual win rate of <strong>80.28%</strong>. Average trade
                P/L was <strong>$92.54</strong>, with a median trade P/L of <strong>$324</strong>.
              </p>

              <p className="text">
                Across <strong>10,000 randomized simulations</strong>, the median ending equity was{' '}
                <strong>$61,895</strong>, closely matching the actual ending equity of <strong>$61638</strong>.
                This suggests the historical result was not heavily dependent on one unusually favorable trade order.
              </p>

              <div className="callout soft">
                <div className="mutedLine">
                  <strong>Actual Result:</strong> $10,000 → <strong>$61,638</strong> —
                  <strong> +518.95%</strong>
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>Median Monte Carlo Ending Equity:</strong> <strong>$61,895</strong>
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>5th / 95th Percentile Ending Equity:</strong>{' '}
                  <strong>$35,225</strong> / <strong>$87,361</strong>
                </div>

                <div style={{ height: 10 }} />

                <div className="stripCallout">
                  <strong>Blowup / Failure Paths:</strong> <strong>224 / 10,000</strong> —
                  <strong> 2.24%</strong>
                </div>
              </div>

              <div style={{ height: 10 }} />

              <p className="stripCallout">
                The key takeaway is that GEMIC’s median randomized outcome closely matched the actual backtest, while
                the lower-tail simulations show why defined risk and controlled sizing still matter.
              </p>
            </div>

            <div className="splitRight">
              <figure className="figure">
                <img
                  className="figureImg"
                  src="/Final%20MC%20Path.png"
                  alt="Monte Carlo equity paths for GEMIC"
                  style={{ width: '100%' }}
                />

                <figcaption className="stripCallout">
                  Monte Carlo equity paths using randomized GEMIC trade sequencing. The simulation highlights path
                  dispersion, drawdown behavior, and lower-tail risk.
                </figcaption>

                <div className="figureDisclaimer">
                  Historical results are shown for illustrative purposes only and do not guarantee future returns.
                </div>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 8) MONTE CARLO SUMMARY ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Monte Carlo Summary</h2>
            <p className="text">
              The Monte Carlo summary focuses on the most important path-dependent outcomes: median performance,
              downside range, drawdown behavior, and failure risk.
            </p>
          </div>

          <div className="kpiGrid">
            <div className="kpiCard">
              <div className="kpiLabel">Simulations</div>
              <div className="kpiValue">10,000</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Median Ending Equity</div>
              <div className="kpiValue">$61,895</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">5th Percentile Equity</div>
              <div className="kpiValue">$35,225</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">95th Percentile Equity</div>
              <div className="kpiValue">$87,361</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Median Max Drawdown</div>
              <div className="kpiValue">-$8,647</div>
              <div className="kpiSub">(-34.08%)</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Worst Max Drawdown</div>
              <div className="kpiValue">-$31,166</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Failure Paths</div>
              <div className="kpiValue">224</div>
              <div className="kpiSub">2.24%</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Median Return</div>
              <div className="kpiValue">+518.95%</div>
            </div>
          </div>

          <div className="stripCallout" style={{ marginTop: 18 }}>
            The Monte Carlo distribution confirms that the strategy’s actual result sits close to the randomized median,
            but the lower-tail outcomes reinforce the importance of sizing discipline and drawdown tolerance.
          </div>
        </div>
      </section>

      {/* ================= 9) BACKTEST STATISTICS ================= */}
      <section className="sectionFull sectionBand kpiSection">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Backtest Statistics</h2>
            <p className="text">
              These results summarize the actual GEMIC backtest and live trades from <strong>April 25, 2023</strong> through{' '}
              <strong>Aug 3, 2026</strong>. The simplified view focuses on growth, win rate, drawdown, and trade-level
              expectancy.
            </p>
          </div>

          <div className="kpiGrid">
            <div className="kpiCard">
              <div className="kpiLabel">Starting Portfolio</div>
              <div className="kpiValue">$10,000</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Ending Portfolio</div>
              <div className="kpiValue">$61,638</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Net P&amp;L</div>
              <div className="kpiValue">+$51,638</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Total Return</div>
              <div className="kpiValue">+516%</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">CAGR</div>
              <div className="kpiValue">74.34%</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Total Trades</div>
              <div className="kpiValue">558</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Win Rate</div>
              <div className="kpiValue">80.28%</div>
              <div className="kpiSub">448 wins / 110 losses</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Average Trade P&amp;L</div>
              <div className="kpiValue">+$92.54</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Median Trade P&amp;L</div>
              <div className="kpiValue">+$324.50</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Max Drawdown</div>
              <div className="kpiValue">-$12,288</div>
              <div className="kpiSub">(-37.29%)</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Sharpe / Sortino</div>
              <div className="kpiValue">1.77 / 1.27</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Profit Factor</div>
              <div className="kpiValue">1.42</div>
            </div>
          </div>

          <div className="stripCallout" style={{ marginTop: 18 }}>
            GEMIC’s edge comes from high win rate and repeatable premium collection, but average losses are materially
            larger than average wins. That makes strike placement, defined risk, and sizing discipline critical to
            long-term survivability.
          </div>
        </div>
      </section>

      {/* ================= 10) FINAL CTA ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Trade the Structure, Not the Guess</h2>
          </div>

          <div className="paperCard">
            <p className="text">
              GEMIC is built for traders who want a systematic SPX premium-selling framework without relying on daily
              directional calls. Inside the SPX Gamma Premium Sellers suite, GEMIC serves as the core daily 1-DTE structure:
              an expected-move-based iron condor designed to keep premium collection consistent, defined, and repeatable.
            </p>

            <p className="text" style={{ marginTop: 10 }}>
              The short strikes are expected-move based. The long wings define the maximum loss. The credit defines the
              maximum profit. Every trade has a mapped payoff profile before execution, allowing GEMIC to function as the
              steady daily layer of the broader premium-selling system.
            </p>

            <p className="text" style={{ marginTop: 10 }}>
              That is the role of GEMIC: disciplined, expected-move-based premium collection using defined-risk SPX iron
              condors,  but the daily structure that anchors the suite with repeatable 1-DTE opportunity.
            </p>

            <div className="heroActions" style={{ marginTop: 16 }}>
              <a
                className="btnGhost"
                href="https://www.spxgamma.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore the SPX Gamma Platform
              </a>
            </div>

            <div className="stripCallout" style={{ marginTop: 16 }}>
              Expected-move based. Defined-risk by construction. Built for disciplined premium selling.
            </div>
            <div className="figureDisclaimer" style={{ marginTop: 16 }}>
              GEMIC trade posts are for educational and informational purposes only. They are not
              financial advice or a recommendation to buy or sell any security or options contract.
              Options trading involves risk and may not be suitable for all traders. Members are
              responsible for their own trade decisions, position sizing, and risk management.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}







